// Single source of truth for fan-page "block" ordering, shared by the club-admin
// My Page editor, the phone preview (FanPagePreview / LiveFanPagePreview) and the
// public fan page (TagPage). Persisted per club in club_settings.block_order.

/** Stable keys for each reorderable fan-page block. */
export type BlockKey =
  | "cta"
  | "predictor"
  | "survey"
  | "poll"
  | "quiz"
  | "match_analytics"
  | "ordering";

/** Canonical default order used when a club has no saved block_order. */
export const DEFAULT_BLOCK_ORDER: BlockKey[] = [
  "cta",
  "predictor",
  "survey",
  "poll",
  "quiz",
  "match_analytics",
  "ordering",
];

/** Human labels for each block (fan-facing / admin). */
export const BLOCK_LABELS: Record<BlockKey, string> = {
  cta: "Call-to-action",
  predictor: "Score predictor",
  survey: "Fan survey",
  poll: "Live poll",
  quiz: "Live quiz",
  match_analytics: "Match analytics",
  ordering: "Food & merch ordering",
};

/** The club_settings boolean column that gates each block (null = always on). */
export const BLOCK_FLAG_COLUMN: Record<BlockKey, string | null> = {
  cta: "cta_enabled",
  predictor: "predictor_enabled",
  survey: "survey_enabled",
  poll: "poll_enabled",
  quiz: "quiz_enabled",
  match_analytics: "match_analytics_enabled",
  ordering: "ordering_enabled",
};

/**
 * Normalise a possibly-missing/partial saved order into a complete, valid,
 * duplicate-free BlockKey[] that always contains every known block. Unknown
 * keys are dropped; missing keys are appended in default order. Safe to call
 * before the block_order column exists (pass null → DEFAULT_BLOCK_ORDER).
 */
export function resolveBlockOrder(saved: string[] | null | undefined): BlockKey[] {
  const known = new Set<BlockKey>(DEFAULT_BLOCK_ORDER);
  const seen = new Set<BlockKey>();
  const ordered: BlockKey[] = [];
  for (const k of saved ?? []) {
    if (known.has(k as BlockKey) && !seen.has(k as BlockKey)) {
      seen.add(k as BlockKey);
      ordered.push(k as BlockKey);
    }
  }
  for (const k of DEFAULT_BLOCK_ORDER) {
    if (!seen.has(k)) ordered.push(k);
  }
  return ordered;
}
